import {DOCUMENT} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs/internal/Observable';
import {environment} from '../../environments/environment';
import {firstValueFrom} from 'rxjs/internal/firstValueFrom';
import {IntercomService} from './intercom.service';
import {Router} from '@angular/router';
import {MixpanelService} from './mixpanel.service';
import {ToastrService} from 'ngx-toastr';
import {PauseDurationMonths} from '../components/modals/update-modal/update-modal.component';
import {AuthService} from './auth.service';
import {UserModel} from '../models/User';

export interface ChurnkeyHashResponse {
    authHash: string;
    customerId: string;
    subscriptionId?: string;
    userId: number;
}

export interface ChurnkeyWindowApi {
    created?: boolean;
    init?: (action: 'show', options: ChurnkeyShowOptions) => void;
    check?: (action: 'failed-payment', options: ChurnkeyFailedPaymentOptions) => void | Promise<void>;
    hide?: () => void;
    clearState?: () => void;
}
type ChurnkeyShowOptions = {
    customerId: string;
    authHash: string;
    subscriptionId?: string;
    appId: string;
    mode: 'live' | 'test';
    provider: 'stripe';
    record?: boolean;

    handleRedirect?: (customer: unknown, res: { redirectUrl?: string }) => Promise<void>;
    onCancel?: (customer: unknown, surveyResponse?: string) => void;
    onDiscount?: (customer: unknown, coupon?: { id?: string }) => void;
    onTrialExtension?: (customer: unknown, data: { trialExtensionDays: number }) => void;
    onGoToAccount?: () => void;
};

type ChurnkeyFailedPaymentOptions = {
    customerId: string;
    authHash: string;
    subscriptionId?: string;
    appId: string;
    provider: 'stripe';
    mode: 'live' | 'test';

    softWall?: boolean;
    gracePeriodDays?: number;
    forceCheck?: boolean;

    handleSupportRequest?: () => void | Promise<void>;
    handleCancel?: () => void | Promise<void>;
    shouldShowFailedPaymentWall?: () => boolean;
    onFailedPaymentWallActivated?: () => void;
};
declare global {
    interface Window {
        churnkey?: ChurnkeyWindowApi;
    }
}

export enum ChurnkeyOutcomeType {
    Cancel = 'cancel',
    Discount = 'discount',
    TrialExtension = 'trial_extension'
}

interface RecordChurnkeyOutcomeResponse {
    success: boolean;
}

interface RecordChurnkeyOutcomePayload {
    type: ChurnkeyOutcomeType;
    subscriptionId?: string;
    customerId?: string;
    metadata?: Record<string, string>;
    couponId?: string;
    trialExtensionDays?: number;
    surveyResponse?: string;
}

const CHURNKEY_URL = '/api/integrations/churnkey';

@Injectable({
    providedIn: 'root'
})
export class ChurnkeyService {
    private loadPromise?: Promise<void>;
    private user: UserModel;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private http: HttpClient,
        private intercom: IntercomService,
        private router: Router,
        private mixpanel: MixpanelService,
        private toastr: ToastrService,
        private authService: AuthService
    ) {
        this.authService.user$.subscribe(user => {
            this.user = user;
        });
    }

    private get hasPaidSubscription(): boolean {
        return !!this.user?.isPaid;
    }

    load(appId: string): Promise<void> {
        if (typeof window === 'undefined') {
            return Promise.resolve();
        }

        if (window.churnkey?.init) {
            return Promise.resolve();
        }

        if (this.loadPromise) {
            return this.loadPromise;
        }

        this.loadPromise = new Promise<void>((resolve, reject) => {
            if (!window.churnkey || !window.churnkey.created) {
                window.churnkey = {created: true};

                const script = this.document.createElement('script');
                script.src = `https://assets.churnkey.co/js/app.js?appId=${encodeURIComponent(appId)}`;
                script.async = true;
                script.onload = () => resolve();
                script.onerror = () => reject(new Error('Failed to load Churnkey script'));

                this.document.head.appendChild(script);
                return;
            }

            const checkReady = () => {
                if (window.churnkey?.init) {
                    resolve();
                } else {
                    setTimeout(checkReady, 50);
                }
            };

            checkReady();
        });

        return this.loadPromise;
    }

    async loadFailedPaymentWall(): Promise<void> {
        const shouldCheckFailedPaymentWall =
            !this.router.url.startsWith('/signup')
            && !this.router.url.startsWith('/onboarding')
            && !this.router.url.startsWith('/login')
            && !this.router.url.startsWith('/upgrade')
            && !this.router.url.startsWith('/account');
        if (!shouldCheckFailedPaymentWall || !this.hasPaidSubscription) {
            window.churnkey?.hide?.();
            return;
        }

        await this.load(environment.churnkeyAppId);

        const hashResponse = await firstValueFrom(this.getHash());
        this.mixpanel.identify(hashResponse.userId.toString());

        if (!window.churnkey?.check) {
            throw new Error('Churnkey failed-payment wall is not available on window');
        }

        this.injectCustomOverrideText();

        await window.churnkey.check('failed-payment', {
            customerId: hashResponse.customerId,
            authHash: hashResponse.authHash,
            subscriptionId: hashResponse.subscriptionId,
            appId: environment.churnkeyAppId,
            provider: 'stripe',
            mode: environment.production ? 'live' : 'test',
            softWall: false,
            gracePeriodDays: 0,
            forceCheck: !environment.production,
            handleSupportRequest: async () => {
                this.mixpanel.track('Clicked Contact Support from Failed Payment Wall')
                this.intercom.openMessenger();
            },
            handleCancel: () => {
                this.mixpanel.track('Clicked Go To Account From Failed Payment Wall');
                window.churnkey?.hide?.();
                void this.router.navigate(['/account/settings']);
            },
            shouldShowFailedPaymentWall: () => true,
            onFailedPaymentWallActivated: () => {
                this.mixpanel.track('Showed Failed Payment Wall')
            }
        });
    }

    private injectCustomOverrideText(): void {
        const observer = new MutationObserver(() => {
            const supportButton = this.document.querySelector('#support-button');
            const goToAccountButton = this.document.querySelector('#cancel-button');
            const header = this.document.querySelector('.ck-step-header');

            if (header) {
                const h1 = header.querySelector('h1');
                const p = header.querySelector('p');

                if (h1) {
                    h1.textContent = 'Your deals are locked due to a failed payment.';
                }

                if (p) {
                    p.textContent = 'We\'ve tried to charge the card on file, but it hasn\'t worked out. Was it replaced or lost? Add a new card to get back to traveling.';
                }
            }
            if (supportButton) {
                supportButton.textContent = 'Contact support';
            }
            if (goToAccountButton) {
                goToAccountButton.textContent = 'Go to account settings';
            }
            if (supportButton && goToAccountButton && header){
                observer.disconnect();
            }
        });

        observer.observe(this.document.body, {
            childList: true,
            subtree: true
        });

        setTimeout(() => observer.disconnect(), 10000);
    }

    getHash(): Observable<ChurnkeyHashResponse> {
        return this.http.post<ChurnkeyHashResponse>(
            `${CHURNKEY_URL}/hash`,
            {},
            {withCredentials: true}
        );
    }

    async openCancelFlow(onPauseRequested?: (pauseDuration: PauseDurationMonths) => void): Promise<void> {
        await this.load(environment.churnkeyAppId);
        window.churnkey.clearState();

        const hashResponse = await firstValueFrom(this.getHash());
        this.mixpanel.identify(hashResponse.userId.toString());
        this.mixpanel.track('Churnkey Downgrade Modal Opened');

        if (!window.churnkey?.init) {
            throw new Error('Churnkey is not available on window');
        }

        window.churnkey.init('show', {
            customerId: hashResponse.customerId,
            authHash: hashResponse.authHash,
            subscriptionId: hashResponse.subscriptionId,
            appId: environment.churnkeyAppId,
            mode: environment.production ? 'live' : 'test',
            provider: 'stripe',
            record: true,

            handleRedirect: (_customer, res) => {
                const pauseDuration = this.getPauseDurationFromRedirectUrl(res?.redirectUrl);

                if (!pauseDuration) {
                    console.warn('Could not determine pause offer from redirectUrl', res?.redirectUrl);
                    return Promise.resolve();
                }

                window.churnkey?.hide?.();
                window.churnkey?.clearState?.();

                onPauseRequested?.(pauseDuration);

                return new Promise(() => {
                    // intentionally unresolved to prevent redirect
                });
            },

            onCancel: (_customer, surveyResponse) => {
                this.mixpanel.track('Downgraded Membership', {
                    surveyResponse
                });

                void this.recordChurnkeyOutcome({
                    type: ChurnkeyOutcomeType.Cancel,
                    subscriptionId: hashResponse.subscriptionId,
                    customerId: hashResponse.customerId,
                    surveyResponse
                }).subscribe({
                    error: (error) => {
                        this.toastr.error(error?.error?.message || error?.message, 'Failed to record cancel outcome');
                    }
                });
            },

            onDiscount: (_customer, coupon) => {
                this.mixpanel.track('Kept Plan With Coupon', {
                    couponId: coupon?.id
                });

                void this.recordChurnkeyOutcome({
                    type: ChurnkeyOutcomeType.Discount,
                    subscriptionId: hashResponse.subscriptionId,
                    customerId: hashResponse.customerId,
                    metadata: {
                        cancellationCouponUsed: 'true'
                    },
                    couponId: coupon?.id
                }).subscribe({
                    error: (error) => {
                        this.toastr.error(error?.error?.message || error?.message, 'Failed to record discount outcome');
                    }
                });
            },

            onTrialExtension: (_customer, {trialExtensionDays}) => {
                this.mixpanel.track('Extended Trial', {
                    trialExtensionDays
                });

                void this.recordChurnkeyOutcome({
                    type: ChurnkeyOutcomeType.TrialExtension,
                    subscriptionId: hashResponse.subscriptionId,
                    customerId: hashResponse.customerId,
                    metadata: {
                        trialExtended: 'true'
                    },
                    trialExtensionDays
                }).subscribe({
                    error: (error) => {
                        this.toastr.error(error?.error?.message || error?.message, 'Failed to record trial extension outcome');
                    }
                });
            },

            onGoToAccount: () => {
                this.router.navigate(['/dashboard']);
            }
        });
    }

    private getPauseDurationFromRedirectUrl(url?: string): PauseDurationMonths | null {
        if (!url) {
            return null;
        }

        try {
            const parsed = new URL(url);
            const path = parsed.pathname.toLowerCase();

            if (path.includes('pause2')) {
                return 2;
            }

            if (path.includes('pause3')) {
                return 3;
            }

            return null;
        } catch (e) {
            this.toastr.error(
                e.message,
                'Unable to load offers'
            );
            return null;
        }
    }

    public recordChurnkeyOutcome(
        payload: RecordChurnkeyOutcomePayload
    ): Observable<RecordChurnkeyOutcomeResponse> {
        return this.http.post<RecordChurnkeyOutcomeResponse>(
            `${CHURNKEY_URL}/subscription/churnkey-outcome`,
            payload,
            {withCredentials: true}
        );
    }
}
